import { Badge, Group, Progress, Stack, Text, Tooltip } from "@mantine/core";
import { IconLoader2 } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useTaskProgress } from "@/hooks/useTaskProgress";
import type { ActiveTask } from "@/types";
import { PERMISSIONS } from "@/types/permissions";
import { elapsedSince, formatElapsed } from "@/utils/duration";
import { getTaskTarget } from "@/utils/tasks";

interface TaskNotificationBadgeProps {
  /**
   * Visual treatment:
   * - `fullwidth` (default): the legacy loud pill that sat at the bottom of
   *   the sidebar. Kept so callers outside the sidebar (and existing tests)
   *   continue to work.
   * - `compact`: a small inline badge sized for a NavLink's `rightSection`.
   *   Shows just the count (or `99+` past two digits), no leading text. Used
   *   by the Sidebar's Tasks row after Phase 8.
   */
  variant?: "fullwidth" | "compact";
}

/**
 * Task notification badge.
 *
 * - `fullwidth`: shows count of active tasks as a full-width pill with a
 *   tooltip containing task details. Visible to anyone with TASKS_READ.
 * - `compact`: a small badge sized for inline use inside a NavLink's right
 *   section. Same tooltip, no full-width pill.
 *
 * Both variants hide entirely when the count is 0 and are gated behind
 * the TASKS_READ permission.
 */
export function TaskNotificationBadge({
  variant = "fullwidth",
}: TaskNotificationBadgeProps = {}) {
  const navigate = useNavigate();
  const { isAdmin, hasPermission } = usePermissions();
  const canReadTasks = hasPermission(PERMISSIONS.TASKS_READ);
  const { activeTasks, pendingCounts } = useTaskProgress();

  // Filter to only running tasks (processing tasks are shown as running).
  // pending tasks are shown separately via pendingCounts, not from activeTasks.
  const runningTasks = activeTasks.filter((task) => task.status === "running");
  const hasRunning = runningTasks.length > 0;

  // Tick once per second only while running tasks exist, so elapsed times
  // refresh without burning CPU when the panel is otherwise idle.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasRunning) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [hasRunning]);

  if (!canReadTasks) {
    return null;
  }

  const totalPendingCount = Object.values(pendingCounts).reduce(
    (sum, count) => sum + count,
    0,
  );

  if (!hasRunning && totalPendingCount === 0) {
    return null;
  }

  const formatTaskType = (type: string) =>
    type
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const renderRunningTask = (task: ActiveTask) => {
    const taskName = formatTaskType(task.taskType);
    const target = getTaskTarget(task);
    const elapsed = formatElapsed(elapsedSince(task.startedAt, now));
    const progressSuffix = task.progress
      ? ` (${task.progress.current}/${task.progress.total})`
      : "";

    // Show the progress bar only when total is positive. Discovery-phase
    // updates emit total=0 while files are still being enumerated, so a bar
    // would be meaningless and would jitter to 0%.
    const total = task.progress?.total ?? 0;
    const current = task.progress?.current ?? 0;
    const showBar = total > 0;
    const percent = showBar ? Math.min(100, (current / total) * 100) : 0;
    const message = task.progress?.message ?? null;

    return (
      <Stack key={task.taskId} gap={2} style={{ minWidth: 240 }}>
        <Group gap="xs" wrap="nowrap" align="center">
          <IconLoader2
            size={12}
            style={{ color: "var(--mantine-color-blue-4)", flexShrink: 0 }}
            className="rotating-icon-small"
          />
          <Text size="xs" style={{ flexShrink: 0 }}>
            {taskName}
            {progressSuffix}
          </Text>
          {target ? (
            <Text
              size="xs"
              c="dimmed"
              title={target}
              style={{
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              · {target}
            </Text>
          ) : null}
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            · {elapsed}
          </Text>
        </Group>
        {showBar ? (
          <Progress value={percent} size="xs" radius="xl" color="blue" />
        ) : null}
        {message ? (
          // RTL ellipsis keeps the meaningful tail of long file paths visible
          // while the leading directories truncate.
          <Text
            size="xs"
            c="dimmed"
            title={message}
            style={{
              direction: "rtl",
              textAlign: "left",
              maxWidth: 240,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {message}
          </Text>
        ) : null}
      </Stack>
    );
  };

  const sortedRunningTasks = [...runningTasks].sort((a, b) => {
    const nameA = formatTaskType(a.taskType);
    const nameB = formatTaskType(b.taskType);
    return nameA.localeCompare(nameB);
  });

  const sortedPendingEntries = Object.entries(pendingCounts)
    .filter(([, count]) => count > 0)
    .sort(([typeA], [typeB]) => {
      const nameA = formatTaskType(typeA);
      const nameB = formatTaskType(typeB);
      return nameA.localeCompare(nameB);
    });

  const tooltipContent = (
    <Stack gap={8}>
      {sortedRunningTasks.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" fw={600}>
            Running Tasks
          </Text>
          {sortedRunningTasks.map(renderRunningTask)}
        </Stack>
      )}

      {sortedPendingEntries.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" fw={600}>
            Pending Tasks ({totalPendingCount})
          </Text>
          {sortedPendingEntries.map(([taskType, count]) => (
            <Group key={taskType} gap="xs">
              <Text size="xs" c="dimmed">
                {formatTaskType(taskType)}: {count}
              </Text>
            </Group>
          ))}
        </Stack>
      )}

      <style>
        {`
					@keyframes rotate-small {
						from { transform: rotate(0deg); }
						to { transform: rotate(360deg); }
					}
					.rotating-icon-small {
						animation: rotate-small 1s linear infinite;
					}
				`}
      </style>
    </Stack>
  );

  const totalTasks = runningTasks.length + totalPendingCount;

  if (variant === "compact") {
    // `99+` past two digits keeps the badge narrow enough to sit inside the
    // Tasks NavLink's rightSection without pushing the icon/label around.
    const display = totalTasks > 99 ? "99+" : String(totalTasks);
    return (
      <Tooltip label={tooltipContent} withArrow position="right">
        <Badge
          color="blue"
          variant="light"
          size="sm"
          radius="sm"
          style={{
            // The whole NavLink is the click target; let the click bubble
            // through the badge instead of capturing it on touch.
            pointerEvents: "none",
          }}
          aria-label={`${totalTasks} pending task${totalTasks !== 1 ? "s" : ""}`}
        >
          {display}
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltipContent} withArrow position="top-start">
      <Badge
        color="blue"
        variant="filled"
        size="sm"
        style={{
          cursor: isAdmin ? "pointer" : "default",
          animation: "pulse 2s ease-in-out infinite",
        }}
        onClick={isAdmin ? () => navigate("/settings/tasks") : undefined}
        fullWidth
      >
        {totalTasks} pending task
        {totalTasks !== 1 ? "s" : ""}
      </Badge>
    </Tooltip>
  );
}
